import * as Mutations from "./mutation-types";
import * as Actions from "./action-types";
import * as api from "@/api/api";
import * as Helpers from "@/helpers";
import * as utils from "@/api/utils";
import { executeQueryHelper } from "@/store/CommonStoreHelpers";
import _ from "lodash";

const defaultProgramsQuery = {
  start_date: "",
  end_date: "",
  columns: [],
  displayColumns: [],
  channels: {},
  groupby: null,
  with_wildcards: true,
  countryTargetGroups: [],
  reachCriteria: "1",
  title: "",
  weekdays: [],
  target_group: [],
  genres: []
};

const defaultProgramsRoFQuery = {
  keys: [],
  return_type: "absolute",
  aggregation_type: "cumulative",
  reachCritera: "1",
  max_frequencies: 10
};

const state = {
  hasSetDefaultProgramsQuery: false,
  programsQueryBeforeChange: {},
  queryCost: {
    data: null,
    isLoading: false,
    error: null
  },
  programsInitStatus: "NOT_LOADED",
  programsQuery: _.cloneDeep(defaultProgramsQuery),
  defaultProgramsQuery: _.cloneDeep(defaultProgramsQuery),
  programsRoFQuery: {
    ...defaultProgramsRoFQuery
  },
  programsQueryForAPI: {},
  programsResults: {
    status: "NOT_LOADED"
  },
  programsRoFResults: {
    status: "NOT_LOADED"
  },
  programsRoFResultsLoadDetails: false,
  programsSummary: [],
  programsColumnTypes: [],
  programsTimeshift: [],
  programsColumnFieldsOptions: [],
  programsDisplayColumns: [],
  programsAllWithStarSelected: true,
  programsWeekdayNumbers: [],
  programsSumVisible: false,
  programsExcludedPrograms: [],
  programsMeasures: [],
  programsStringForFilterBeforeChange: ""
};

const PrepareQuery = (context, showGroupByDropdowns) => {
  let query = {
    ...context.getters.getProgramsQueryForAPI
  };
  // om vi inte renderat datat, så finns inte dessa dropdowns
  let aggregationTypeDropdowns =
    context.getters.getProgramsResults?.data?.aggregationTypeDropdowns;
  if (aggregationTypeDropdowns != null) {
    query.measures = query.measures.map(measure => ({
      ...measure,
      default_aggregation: showGroupByDropdowns
        ? aggregationTypeDropdowns
            .find(t => t.key === measure.display_name)
            .value.toUpperCase()
        : "AVG"
    }));
  }
  query.sort_by = context.state.programsQueryForAPI.sort_by;
  query.sort_direction = context.state.programsQueryForAPI.sort_direction;
  query.last_row_values = null;
  query.showGroupByDropdowns = showGroupByDropdowns;
  query.excludedRows = context.getters.getProgramsExcludedPrograms;
  return query;
};

const convertFromQueryToQueryForAPI = (
  ctx,
  q,
  key_column,
  options = {
    sortBy: null,

    sort_direction: "ASC",
    rows_per_page: 10000000
  }
) => {
  let channels = q.channels;

  const defaultProgramsQueryForAPI = {
    rows_per_page: options.rows_per_page,
    sort_by: options.sortBy,
    sort_direction: options.sort_direction,
    columns: [],
    displayColumns: [],
    measures: [],
    min_slot_length: 0,
    filters: null,
    genres: q.genres,
    orig_codes: channels
      ? Object.keys(channels).reduce(
          (prev, key) => [...prev, ...channels[key]],
          []
        )
      : [],
    key_column: {
      column_type: key_column.column_type,
      display_name: key_column.display_name
    },
    last_row_values: null,
    tab: "program",
    search: q.title
      ? [
          {
            with_wildcard: q.with_wildcards,
            search_text: q.title,
            column: {
              column_type: "'dim_program'[title]",
              display_name: "Titel"
            }
          }
        ]
      : [],
    weekday_numbers: [],
    countryTargetGroups: q.countryTargetGroups
  };
  const defaultColumn = {};
  // är inga groupbycolumns valda så skickar vi med alla kolumner
  var columns = q.columns;
  if (columns.length == 0) {
    columns = ctx.state.programsColumnTypes.filter(x => x.hidden == false);
  }
  const defaultMeasure = {
    min_viewing_time: 0
  };
  // + skicka alltid med alwaysIncludeInGroupBy, om de inte redan finns
  columns = columns.concat(ctx.state.programsColumnTypes
    .filter(col => col.alwaysIncludeInGroupBy && !columns.some(c => c.column_type === col.column_type)));

  columns = columns.map(x => ({ column_type: x.column_type, display_name: x.display_name }));
  let startTimeForQuery = "";
  let onlyStartTime =
  q.start_time?.replace(/\s+/g, "").length === 4;
  if(onlyStartTime)
    startTimeForQuery = `${q.start_time}-${q.start_time}`
  else
    startTimeForQuery = q.start_time
  const APIQuery = {
    ...defaultProgramsQueryForAPI,
    start_date: q.start_date ?? null,
    end_date: q.end_date ?? null,
    start_time: startTimeForQuery,
    min_slot_length: q.duration ? q.duration * 60 : 0,
    reach_criteria: q.reachCriteria,
    measures: q.target_group
    ? q.target_group.map(measure => {
        let targetGroup = {
          group: measure.group == 'W' ? 'K' : measure.group,
          age_from: measure.age_from,
          age_to: measure.age_to
        };
        
        // Add Other target group fields if group is "O"
        if (measure.group === 'O' && measure.othertargetgroups && measure.othertargetgroupsvalues) {
          targetGroup.other_category = measure.othertargetgroups;
          targetGroup.other_value = measure.othertargetgroupsvalues;
        }
        
        return {
          ...defaultMeasure,
          min_viewing_time: measure.reach_criteria,
          target_group: targetGroup,
          consolidated: measure.timeshift.consolidated,
          days: measure.timeshift.days,
          timeshift: measure.timeshift.keys,
          display_name: Helpers.createMeasureName(measure),
          column_type: measure.measure.column_type
        };
      })
  : [],
    columns: columns
      ? columns.map(column => ({
          ...defaultColumn,
          ...column
        }))
      : [],
    displayColumns: q.displayColumns
      ? q.displayColumns.map(column => ({
        ...defaultColumn,
        ...column
      }))
      : [],
    weekday_numbers: q.weekdays
  };

  return APIQuery;
};

const executeRoFQuery = async (context, query) => {
  try {
    var r = await api.executeProgramsRoFQuery(query);
    // fått ett svar

    // kolla om vi fått några rader
    if (!(r.keys && r.keys.length > 0)) {
      context.commit(Mutations.setProgramsRoFResults, {
        status: "EMPTY_RESULT"
      });
    } else {
      // spara resultaten formaterat enligt query

      const formatFunc = val =>
        query.return_type == "absolute" // absoluta värden visas i tusental och avrundat
          ? Math.round(val / 1000, 0).toLocaleString("sv-Sv") // relativa värden visas i procen med 1 decimal
          : `${Math.round(val * 1000) / 10}`;

      context.commit(Mutations.setProgramsRoFResults, {
        status: "LOADED",
        data: {
          ...r,
          // formatera frekvensmatrisen
          frequency_matrix: r.frequency_matrix.map(row =>
            row.map(cell => formatFunc(cell))
          ),
          reach_1_plus_acc: r.reach_1_plus_acc.map(cell => formatFunc(cell)),
          average_frquency: r.average_frquency.map(cell => cell.toFixed(1).replace('.', ',')),
          rating: r.rating.map(cell => formatFunc(cell))
        }
      });
    }
  } catch (error) {
    context.commit(Mutations.setProgramsRoFResults, {
      status: "FAILED",
      data: null,
      error: error
    });
  }
};

// hjälpfunktion för att exekvera programfrågan
const executeQuery = async (context, query, addRows = false) => {
  await executeQueryHelper(context, query, {
    addRows,
    moduleName: "Programs",
    mutations: Mutations,
    getters: context.getters,
    executeQueryFn: api.executeQueryDataQuery
  });
};

const prepareQuery = async context => {
  const selColumns = context.getters["getProgramsQuery"].columns;
  const columnFieldsOptions = context.getters["getProgramsColumnFieldsOptions"];

  const allStarSelected = Helpers.allWithStarsSelected(
    selColumns,
    columnFieldsOptions
  );

  await context.commit(
    Mutations.setProgramsAllWithStarSelected,
    allStarSelected
  );

  // konvertera till fråga för API
  // TODO - OM man inte valt alla * i kolumnväljaren; så ska man skicka tom keycolumn (som överst i exemplet)
  let keycolumn = null;

  if (allStarSelected) {
    keycolumn = context.state.programsColumnTypes.filter(col => col.key)[0];
  } else {
    keycolumn = {
      column_type: null,
      display_name: null
    };
  }

  const columnsWithoutKey = context.state.programsColumnTypes
    .filter(col => !col.key)
    .map(x => x.display_name);
  context.state.programsQuery.columns.sort(function(a, b) {
    return (
      columnsWithoutKey.indexOf(a.display_name) -
      columnsWithoutKey.indexOf(b.display_name)
    );
  });
  const query = convertFromQueryToQueryForAPI(
    context,
    context.state.programsQuery,
    keycolumn
  );
  // comitta queryn till store (så att den används vid exporten)
  await context.commit(Mutations.setProgramsQueryForAPI, query);

  return query;
};

const GetRoFQuery = context => {
  const keycolumn = context.state.programsColumnTypes.filter(col => col.key)[0];
  // konvertera till fråga för API
  // keysobjektet: [[{DAXIdentifier,Value},...]]
  const query = {
    keys: context.state.programsRoFQuery.keys.map(key => [
      {
        DAXIdentifier: keycolumn.column_type,
        Value: `${key}`
      }
    ]),
    panel_date: context.state.programsRoFQuery.panel_date,
    reach_criteria: context.state.programsRoFQuery.reach_criteria,
    return_type: context.state.programsRoFQuery.return_type,
    aggregation_type: context.state.programsRoFQuery.aggregation_type,
    max_frequencies: context.state.programsRoFQuery.max_frequencies,
    target_group: context.state.programsRoFQuery.target_group_rof,
    start_date: context.state.programsRoFQuery.start_date,
    end_date: context.state.programsRoFQuery.end_date
  };

  return query;
};

const actions = {
  // hämta alla data som krävs för att visa sidan
  // uppdatera state programInitStatus för att kunna ha spinners etc vid laddning

  [Actions.getProgramsColumnTypes]: async () => {
    return await api.getColumnTypes("PROGRAMS");
  },
  [Actions.getProgramsMeasures]: async () => {
    return await api.getMeasures("PROGRAMS");
  },

  [Actions.getProgramsChannels]: async () => {
    let dateQuery = {
      start_date: state.programsQuery.start_date,
      end_date: state.programsQuery.end_date
    };

    return await api.getAllChannels(dateQuery);
  },
  [Actions.getProgramsGenres]: async () => {
    return await api.getGenres();
  },

  [Actions.getProgramsTimeshift]: async () => {
    const timeshift = await api.getTimeshift();
    // här tar vi just nu bara de för aggregerade timeshifts,
    // alltså KO, OV, LIVE osv. och grupperar ihop

    return timeshift;
    //await context.commit(Mutations.setProgramsTimeshift, groupedTimeshift)
  },

  // denna initialiserar tabellen och frågan, alltså använda då tryck på 'Sök'
  [Actions.executeProgramsQuery]: async context => {
    context.commit(Mutations.setProgramsResults, {
      status: "LOADING",
      data: null
    });
    // initiera/resetta selected rows
    context.commit(Mutations.setProgramsExcludedPrograms, []);

    let query = await prepareQuery(context);

    // kolla om frågan är OK att köra och returnera till gränssnittet, annars sätt queryCost så tar vi det därifrån
    context.commit(Mutations.setQueryCostLoading, true);
    context.commit(Mutations.setQueryCostError, null);

    try {
      const queryCostData = await api.getQueryCost(query);
      await context.commit(Mutations.setQueryCostData, queryCostData);
    } catch (error) {
      context.commit(Mutations.setProgramsResults, {
        status: "VALIDATION_FAILED"
      });
      await context.commit(Mutations.setQueryCostError, error.response.data.message);
      return;
    } finally {
      await context.commit(Mutations.setQueryCostLoading, false);
    }
    if (
      context.state.queryCost.error == null &&
      context.state.queryCost.data.queryClass == "Normal"
    ) {
      await executeQuery(context, query, false);
    } else {
      context.commit(Mutations.setProgramsResults, {
        status: "NOT_LOADING",
        data: null
      });
    }
  },

  [Actions.executeScheduleQuery]: async (context, reportname) => {
    let query = PrepareQuery(context, false);
    await api.createScheduledReportQuery(reportname, query);
  },

  [Actions.executeExportProgramsQuery]: async (
    context,
    showGroupByDropdowns
  ) => {
    let query = PrepareQuery(context, showGroupByDropdowns);
    return await api.executeExportQuery(query);
  },
  [Actions.executeProgramsRoFQuery]: async context => {
    context.commit(Mutations.setProgramsRoFResults, {
      status: "LOADING",
      data: null
    });

    const query = GetRoFQuery(context);
    await executeRoFQuery(context, query);
  },
  [Actions.executeProgramsQueryMoreExclude]: async (
    context,
    rowsWithoutExcludedRows
  ) => {
    context.commit(Mutations.setProgramsResults, {
      ...context.state.programsResults,
      data: {
        ...context.state.programsResults.data,
        rows: rowsWithoutExcludedRows
      },
      loadingTable: false
    });
  },
  [Actions.changeSortPrograms]: async (context, payload) => {
    let columnInfo = context.getters.getProgramsResults.data.columns.find(
      c => c.key == payload.sortBy
    );
    let tableRows = [
      ...context.getters.getProgramsResults.data.rows.filter(p => !p.type)
    ];
    if (columnInfo.isNumber) {
      if (payload.sortDesc)
        tableRows.sort((a, b) => {
          let first = a[payload.sortBy];
          let second = b[payload.sortBy];
          if (typeof first === "string")
            first = parseFloat(first.replace(/\s/g, "").replace(/,/g, "."));
          if (typeof second === "string")
            second = parseFloat(second.replace(/\s/g, "").replace(/,/g, "."));
          return second - first;
        });
      else {
        tableRows.sort((a, b) => {
          let first = a[payload.sortBy];
          let second = b[payload.sortBy];
          if (typeof first === "string")
            first = parseFloat(first.replace(/\s/g, "").replace(/,/g, "."));
          if (typeof second === "string")
            second = parseFloat(second.replace(/\s/g, "").replace(/,/g, "."));
          return first - second;
        });
      }
    } else if (columnInfo.isPercentage) {
      if (payload.sortDesc) {
        tableRows.sort((a, b) => {
          a = parseFloat(
            a[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          b = parseFloat(
            b[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          if (a < b) {
            return 1;
          }
          if (a > b) {
            return -1;
          }
          return 0;
        });
      } else {
        tableRows.sort((a, b) => {
          a = parseFloat(
            a[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          b = parseFloat(
            b[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          if (a < b) {
            return -1;
          }
          if (a > b) {
            return 1;
          }
          return 0;
        });
      }
    } else if (columnInfo.isWeekday) {
      utils.sortAsWeekday(tableRows, payload);
    } else {
      if (payload.sortDesc) {
        tableRows.sort((a, b) => {
          a = a[payload.sortBy];
          b = b[payload.sortBy];
          if (a < b || a == null) {
            return 1;
          }
          if (a > b || b == null) {
            return -1;
          }
          return 0;
        });
      } else {
        tableRows.sort((a, b) => {
          a = a[payload.sortBy];
          b = b[payload.sortBy];
          if (a < b || a == null) {
            return -1;
          }
          if (a > b || b == null) {
            return 1;
          }
          return 0;
        });
      }
    }
    let summaryArray = context.getters.getProgramsResults.data.summary.array.map(
      obj => {
        return {
          ...obj,
          visible: false
        };
      }
    );
    tableRows.unshift(
      ...context.getters.getProgramsResults.data.rows.filter(p => p.type)
    );
    context.commit(Mutations.setProgramsResults, {
      ...state.programsResults,
      data: {
        ...context.state.programsResults.data,
        rows: tableRows,
        summary: {
          ...context.state.programsResults.data.summary,
          array: summaryArray
        }
      },
      loadingTable: false
    });

    const sortDir = payload.sortDesc === true ? "DESC" : "ASC";
    // skapa kolumnobjekt från data-objektet
    const column = context.getters.getProgramsResults.data.columns.filter(
      c => c.key == payload.sortBy
    )[0];
    const sortByColumn = (({ column_type, key }) => ({
      column_type,
      display_name: key
    }))(column);
    context.commit(Mutations.setProgramsQueryForAPI, {
      ...context.getters.getProgramsQueryForAPI,
      sort_by: sortByColumn,
      sort_direction: sortDir
    });
  }
};

const getters = {
  getHasSetDefaultProgramsQuery: () => state.hasSetDefaultProgramsQuery,
  getDefaultProgramsQuery: () => state.defaultProgramsQuery,
  getProgramsQuery: state => state.programsQuery,
  getProgramsRoFQuery: state => state.programsRoFQuery,
  getProgramsResults: state => state.programsResults,
  getProgramsRoFResultsLoadDetails: state =>
    state.programsRoFResultsLoadDetails,
  getProgramsExcludedPrograms: state => state.programsExcludedPrograms,
  getProgramsRoFResults: state => state.programsRoFResults,
  getProgramsSummary: state => state.programsSummary,
  getProgramsQueryForAPI: state => state.programsQueryForAPI,
  getProgramsColumnTypes: ({ programsColumnTypes }) => programsColumnTypes,
  getProgramsChannels: ({ programsChannels }) => programsChannels,
  getProgramsInitStatus: ({ programsInitStatus }) => programsInitStatus,
  getProgramsColumnFieldsOptions: state => state.programsColumnFieldsOptions,
  getProgramsAllWithStarSelected: state => state.programsAllWithStarSelected,
  getProgramsSumVisible: state => state.programsSumVisible,
  getProgramsQueryCost: state => state.queryCost,
  getProgramsMeasures: state => state.programsMeasures,
  getProgramsQueryBeforeChange: state => state.programsQueryBeforeChange,
  getProgramsStringForFilterBeforeChange: state =>
    state.programsStringForFilterBeforeChange
};

const mutations = {
  [Mutations.setHasSetDefaultProgramsQuery]: (state, payload) =>
    (state.hasSetDefaultProgramsQuery = payload),
  [Mutations.setDefaultProgramsQuery]: (state, payload) =>
    (state.defaultProgramsQuery = payload),
  [Mutations.setProgramsExcludedPrograms]: (state, payload) =>
    (state.programsExcludedPrograms = payload),
  [Mutations.setProgramsQuery]: (state, payload) =>
    (state.programsQuery = payload),
  [Mutations.setProgramsRoFQuery]: (state, payload) =>
    (state.programsRoFQuery = payload),
  [Mutations.setProgramsResults]: (state, payload) =>
    (state.programsResults = payload),
  [Mutations.setProgramsResultsColumns]: (state, payload) =>
    (state.programsResults.data.columns = payload),
  [Mutations.setProgramsSummary]: (state, payload) =>
    (state.programsSummary = payload),
  [Mutations.setProgramsColumnTypes]: (state, payload) =>
    (state.programsColumnTypes = payload),
  [Mutations.setProgramsInitStatus]: (state, payload) =>
    (state.programsInitStatus = payload),
  [Mutations.setProgramsQueryForAPI]: (state, payload) =>
    (state.programsQueryForAPI = payload),
  [Mutations.setProgramsTimeshift]: (state, payload) =>
    (state.programsTimeshift = payload),
  [Mutations.setProgramsChannels]: (state, payload) =>
    (state.programsChannels = payload),
  [Mutations.setProgramsRoFResults]: (state, payload) =>
    (state.programsRoFResults = payload),
  [Mutations.setProgramsStartdate]: (state, payload) =>
    (state.programsQuery.start_date = payload),
  [Mutations.setProgramsEnddate]: (state, payload) =>
    (state.programsQuery.end_date = payload),
  [Mutations.setProgramsGenres]: (state, payload) =>
    (state.programsGenres = payload),
  [Mutations.setProgramsGroupBy]: (state, payload) =>
    (state.programsQuery.groupby = payload),
  [Mutations.setProgramsColumnFieldsOptions]: (state, payload) =>
    (state.programsColumnFieldsOptions = payload),
  [Mutations.setProgramsDisplayColumnFieldsOptions]: (state, payload) =>
    (state.programsDisplayColumnFieldsOptions = payload),
  [Mutations.setProgramsAllWithStarSelected]: (state, payload) =>
    (state.programsAllWithStarSelected = payload),
  [Mutations.setProgramsWeekdays]: (state, payload) =>
    (state.programsWeekdayNumbers = payload),
  [Mutations.setProgramsRoFResultsLoadDetails]: (state, payload) =>
    (state.programsRoFResultsLoadDetails = payload),
  [Mutations.setProgramsSumVisible]: (state, payload) =>
    (state.programsSumVisible = payload),
  [Mutations.setQueryCostData]: (state, payload) =>
    (state.queryCost.data = payload),
  [Mutations.setQueryCostLoading]: (state, payload) =>
    (state.queryCost.loading = payload),
  [Mutations.setQueryCostError]: (state, payload) =>
    (state.queryCost.error = payload),
  [Mutations.setProgramsMeasures]: (state, payload) =>
    (state.programsMeasures = payload),
  [Mutations.setProgramsQueryBeforeChange]: (state, payload) =>
    (state.programsQueryBeforeChange = payload),
  [Mutations.setProgramsStringForFilterBeforeChange]: (state, payload) =>
    (state.programsStringForFilterBeforeChange = payload)
};

export default {
  state,
  getters,
  mutations,
  actions
};
